import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Article as ArticleIcon,
  AssessmentOutlined as AssessmentOutlinedIcon,
  Business as BusinessIcon,
  BusinessCenterOutlined as BusinessCenterOutlinedIcon,
  Close as CloseIcon,
  FileDownload as FileDownloadIcon,
  GroupsOutlined as GroupsOutlinedIcon,
  PictureAsPdf as PictureAsPdfIcon,
  TableChart as TableChartIcon,
} from '@mui/icons-material';
import {
  formatDate,
  formatPartnerSource,
  getEvaluationMeta,
  getSituationChipSx,
  getSituationValue,
  isValidValue,
  normalizeFinancement,
  normalizeEvalResult,
  normalizeText,
} from '../../../helpers/PartnerHelper';
import EvaluationService from '../../../services/EvaluationService';
import PartnerExportService from '../../../services/PartnerExportService';
import { getSpmPID } from '../../../helpers/PeidGeneratorHelper';
import {
  exportPartnerEvaluationExcel,
  exportPartnerEvaluationPdf,
  formatCount,
  normalizeExportSituation,
} from './PartnerEvaluationExport';

const PERSONNEL_PAGE_SIZE = 8;

const parseNumberValue = (value) => {
  if (!isValidValue(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatPartnerMatricule = (pid) => {
  if (!isValidValue(pid)) return '-';
  return /^SPM-/i.test(String(pid)) ? getSpmPID(pid) || '-' : pid;
};

const formatPersonnelMatricule = (person) => {
  return isValidValue(person?.peid) ? person.peid : '-';
};

const PartnerInfo = ({ label, value }) => (
  <Box>
    <Typography sx={{ color: '#667085', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography sx={{ color: '#101828', fontSize: 14, mt: 0.3, overflowWrap: 'anywhere' }}>
      {isValidValue(value) ? value : '—'}
    </Typography>
  </Box>
);

const dedupeEvaluations = (evaluations) => {
  const unique = new Map();

  evaluations.forEach((evaluation) => {
    const key = [
      normalizeEvalResult(evaluation.result),
      evaluation.date || '',
      normalizeText(evaluation.situation || ''),
    ].join('|');

    if (!unique.has(key)) {
      unique.set(key, evaluation);
    }
  });

  return Array.from(unique.values());
};

const normalizePartnerEvaluationRows = (source) => {
  if (Array.isArray(source)) return source;
  if (!source || typeof source !== 'object') return [];

  const base = {
    partenaire_id: source.partenaire_id,
    pid: source.pid,
    partenaire: source.partenaire,
    statutJuridique: source.statutJuridique,
    contact: source.contact,
    emailPartenaire: source.emailPartenaire,
    telephone: source.telephone,
    nif: source.nif,
    stat: source.stat,
    source_db: source.source_db,
  };
  const marches = Array.isArray(source.marches) ? source.marches : [];
  const evaluations = Array.isArray(source.evaluations) ? source.evaluations : [];
  const personnel = Array.isArray(source.personnel) ? source.personnel : [];
  const findMarket = (entry) =>
    marches.find((market) => (
      (isValidValue(entry?.execution_marche_id)
        && String(entry.execution_marche_id) === String(market.execution_marche_id))
      || (isValidValue(entry?.numeroMarche) && String(entry.numeroMarche) === String(market.numeroMarche))
    )) || {};

  const marketRows = marches.map((market) => ({
    ...base,
    ...market,
  }));

  const evaluationRows = evaluations.map((evaluation) => ({
    ...base,
    ...findMarket(evaluation),
    ...evaluation,
  }));

  const personnelRows = personnel.map((person) => ({
    ...base,
    ...findMarket(person),
    ...person,
  }));

  const rows = [...marketRows, ...evaluationRows, ...personnelRows];
  if (rows.length > 0) return rows;

  return [source];
};

const buildPartnerEvaluation = (source, partnerName, partnerPid = '', sourceDb = '') => {
  const rows = normalizePartnerEvaluationRows(source);
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const filteredRows = isValidValue(partnerName)
    ? rows.filter((row) => String(row.partenaire || '').trim() === String(partnerName).trim())
    : [];
  const selectedRows = filteredRows.length > 0 ? filteredRows : rows;

  const first = selectedRows[0];
  const resolvedPartnerName = isValidValue(partnerName) ? partnerName : first.partenaire;
  const directions = new Set();
  const situations = new Set();
  const marches = new Map();
  const evaluations = [];
  const personnelMap = new Map();
  const financements = new Set();
  const composantes = new Set();

  selectedRows.forEach((row) => {
    if (isValidValue(row.direction)) {
      directions.add(String(row.direction).trim());
    }

    if (isValidValue(row.financement)) {
      financements.add(normalizeFinancement(row.financement));
    }

    if (isValidValue(row.composante)) {
      composantes.add(String(row.composante).trim());
    }

    situations.add(normalizeText(row.situation || ''));

    const marketNumber = isValidValue(row.numeroMarche) ? String(row.numeroMarche).trim() : '';
    if (marketNumber) {
      const situationValue = getSituationValue(new Set([normalizeText(row.situation || '')]));
      const beneficiairesValue = parseNumberValue(
        row.beneficiaires ?? row.nbBeneficiaires ?? row.nbMenages ?? row.nbMenage
      );

      if (!marches.has(marketNumber)) {
        marches.set(marketNumber, {
          num: marketNumber,
          financement: normalizeFinancement(row.financement),
          composante: row.composante || '',
          direction: row.direction || '',
          objet: row.objet || '',
          zone: row.zone || '',
          beneficiaires: beneficiairesValue,
          dateDebut: row.dateDebut || null,
          dateFin: row.dateFin || null,
          situation: situationValue,
        });
      } else if (beneficiairesValue !== null) {
        const existingMarket = marches.get(marketNumber);
        if (existingMarket && existingMarket.beneficiaires === null) {
          existingMarket.beneficiaires = beneficiairesValue;
        }
      }
    }

    const evalResult = normalizeEvalResult(row.resultatEvaluation);
    if (evalResult) {
      evaluations.push({
        result: evalResult,
        date: isValidValue(row.dateEvaluation) ? String(row.dateEvaluation) : null,
        marche: marketNumber,
        situation: normalizeText(row.situation || ''),
      });
    }

    if (isValidValue(row.nomPersonnel) && !personnelMap.has(row.nomPersonnel)) {
      personnelMap.set(row.nomPersonnel, {
        nom: row.nomPersonnel,
        peid: row.peid || '',
        personKey: row.personKey || row.person_key_source || '',
        cin: row.cin || '',
        fonction: row.fonction || '',
        email: row.emailPersonnel || '',
        telephone: row.telPersonnel || row.telephonePersonnel || '',
        dateDebut: row.dateDebut || null,
        dateFin: row.dateFin || null,
        situation: normalizeText(row.situation || '') || 'FIN',
      });
    }
  });

  const uniqueEvaluations = dedupeEvaluations(evaluations);
  const sortedEvaluations = [
    ...uniqueEvaluations.filter((evaluation) => evaluation.date).sort((a, b) => b.date.localeCompare(a.date)),
    ...uniqueEvaluations.filter((evaluation) => !evaluation.date),
  ];

  const marketRows = Array.from(marches.values()).sort((a, b) => a.num.localeCompare(b.num, 'fr'));
  const marchesEnCours = marketRows.filter((market) => market.situation === 'EN COURS').length;
  const marchesResilies = marketRows.filter((market) => market.situation === 'RESILIE').length;
  const hasBeneficiaires = marketRows.some((market) => Number.isFinite(market.beneficiaires));
  const totalBeneficiaires = hasBeneficiaires
    ? marketRows.reduce((sum, market) => sum + (Number(market.beneficiaires) || 0), 0)
    : null;
  const beneficiairesEnCours = hasBeneficiaires
    ? marketRows
        .filter((market) => market.situation === 'EN COURS')
        .reduce((sum, market) => sum + (Number(market.beneficiaires) || 0), 0)
    : null;
  const situation = getSituationValue(situations);
  const personnel = Array.from(personnelMap.values()).sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
  const hasSuiviMarche = selectedRows.some((row) => (
    normalizeText(row.source_db || '').includes('SUIVI MARCHE')
  ));
  const hasPartnerDatabase = Boolean(first.partenaire_id || first.partenaireId)
    || selectedRows.some((row) => normalizeText(row.source_db || '').includes('PARTENAIRE'));
  const inferredSourceDb = hasSuiviMarche && hasPartnerDatabase
    ? 'bdd suivi marché / bdd partenaire'
    : hasSuiviMarche
      ? 'bdd suivi marché'
      : 'bdd partenaire';

  const partenaireId = first.partenaire_id || first.partenaireId || null;

  return {
    partenaireId,
    pid: partenaireId
      ? (first.pid || partnerPid || '')
      : (getSpmPID(partnerPid) || getSpmPID(first.pid) || ''),
    name: String(resolvedPartnerName || '').trim(),
    statutJuridique: first.statutJuridique || '',
    nif: first.nif || '',
    stat: first.stat || '',
    contact: first.contact || '',
    email: first.emailPartenaire || first.email || '',
    tel: first.telephone || first.tel || '',
    dirs: Array.from(directions).sort((a, b) => a.localeCompare(b, 'fr')),
    financements: Array.from(financements).sort((a, b) => a.localeCompare(b, 'fr')),
    composantes: Array.from(composantes).sort((a, b) => a.localeCompare(b, 'fr')),
    situation,
    sourceDb: sourceDb || inferredSourceDb,
    nbMarches: marketRows.length,
    marchesEnCours,
    marchesResilies,
    totalBeneficiaires,
    beneficiairesEnCours,
    evalResult: sortedEvaluations[0]?.result || '',
    evalDate: sortedEvaluations[0]?.date || null,
    evalHist: sortedEvaluations.slice(0, 6),
    marches: marketRows,
    personnel,
  };
};

const PartnerEvalution = ({
  open = false,
  onClose = () => {},
  partnerId = null,
  partnerPid = '',
  partnerName = '',
  sourceDb = '',
  data = null,
  onExportExcel,
  onExportPdf,
}) => {
  const [remoteRows, setRemoteRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [personnelNameFilter, setPersonnelNameFilter] = useState('');
  const [personnelPositionFilter, setPersonnelPositionFilter] = useState('');
  const [personnelPage, setPersonnelPage] = useState(1);

  useEffect(() => {
    if (!open) {
      setIsLoading(false);
      setLoadError('');
      return;
    }

    if (data && (Array.isArray(data) || typeof data === 'object')) {
      setRemoteRows([]);
      setIsLoading(false);
      setLoadError('');
      return;
    }

    if (!isValidValue(partnerId) && !isValidValue(partnerName)) {
      setRemoteRows([]);
      setIsLoading(false);
      setLoadError('');
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    const request = isValidValue(partnerId)
      ? EvaluationService.getPartnerRows(partnerId)
      : EvaluationService.getPartnerRowsByName(partnerName, 'Partenaires');

    request
      .then((result) => {
        if (!isMounted) return;

        const nextRows = result?.data || [];
        const hasRows = Array.isArray(nextRows)
          ? nextRows.length > 0
          : nextRows && typeof nextRows === 'object' && Object.keys(nextRows).length > 0;
        const message = typeof result?.message === 'string' ? result.message.trim() : '';
        setRemoteRows(nextRows);
        if (
          !hasRows &&
          result?.status === 0 &&
          message !== '' &&
          !/^aucun/i.test(message) &&
          !/^aucune/i.test(message)
        ) {
          setLoadError(result.message);
        }
      })
      .catch(() => {
        if (!isMounted) return;

        setRemoteRows([]);
        setLoadError("Impossible de charger la fiche d'évaluation.");
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [data, open, partnerId, partnerName]);

  const sourceRows = data || remoteRows;
  const detail = useMemo(
    () => buildPartnerEvaluation(sourceRows || [], partnerName, partnerPid, sourceDb),
    [sourceRows, partnerName, partnerPid, sourceDb]
  );
  const filteredPersonnel = useMemo(() => {
    if (!detail) return [];

    const normalizedName = normalizeText(personnelNameFilter);
    const normalizedPosition = normalizeText(personnelPositionFilter);
    return detail.personnel.filter((person) => (
      (!normalizedName || normalizeText(person.nom).includes(normalizedName))
      && (!normalizedPosition || normalizeText(person.fonction).includes(normalizedPosition))
    ));
  }, [detail, personnelNameFilter, personnelPositionFilter]);
  const personnelPageCount = Math.max(
    1,
    Math.ceil(filteredPersonnel.length / PERSONNEL_PAGE_SIZE)
  );
  const paginatedPersonnel = useMemo(() => {
    const start = (personnelPage - 1) * PERSONNEL_PAGE_SIZE;
    return filteredPersonnel.slice(start, start + PERSONNEL_PAGE_SIZE);
  }, [filteredPersonnel, personnelPage]);

  useEffect(() => {
    setPersonnelNameFilter('');
    setPersonnelPositionFilter('');
    setPersonnelPage(1);
  }, [open, partnerId, partnerName]);

  useEffect(() => {
    setPersonnelPage(1);
  }, [personnelNameFilter, personnelPositionFilter]);

  useEffect(() => {
    if (personnelPage > personnelPageCount) setPersonnelPage(1);
  }, [personnelPage, personnelPageCount]);
  const generatedAt = useMemo(() => new Date().toLocaleDateString('fr-FR'), []);
  const generatedDate = useMemo(() => new Date().toISOString().split('T')[0], []);
  const paperGlowSx = {
    borderRadius: '8px',
    border: '1px solid #cbd8e6',
    boxShadow: 'none',
  };
  const heroPaperSx = {
    ...paperGlowSx,
    p: { xs: 2, md: 3 },
    color: '#10263d',
    border: '1px solid #cbd8e6',
    background: '#ffffff',
  };
  const cardPaperSx = {
    ...paperGlowSx,
    p: 2,
    background: '#ffffff',
    height: '100%',
  };
  const miniPaperSx = {
    borderRadius: '8px',
    border: '1px solid #d5e0eb',
    boxShadow: 'none',
    p: 1.5,
    height: '100%',
    background: '#ffffff',
  };
  const sectionTitleSx = {
    mb: 1.5,
    fontWeight: 800,
    color: '#173d61',
    letterSpacing: 0,
  };
  const tableHeadCellSx = {
    fontWeight: 800,
    fontSize: 11,
    color: '#ffffff',
    bgcolor: '#173d61',
    textTransform: 'uppercase',
    borderBottom: '1px solid #d5e0eb',
  };
  const metaLabelSx = {
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    fontSize: 11,
    fontWeight: 700,
  };
  const mutedChipSx = {
    borderColor: '#cbd5e1',
    bgcolor: '#f8fafc',
    color: '#334155',
    fontWeight: 700,
  };
  const metricIconSx = {
    alignItems: 'center',
    bgcolor: '#e8eef7',
    borderRadius: '8px',
    color: '#173d61',
    display: 'flex',
    height: 38,
    justifyContent: 'center',
    width: 38,
  };

  const handleExportExcel = () => {
    if (!detail) return;

    if (typeof onExportExcel === 'function') {
      onExportExcel(detail);
      return;
    }

    exportPartnerEvaluationExcel(detail, generatedDate);
    PartnerExportService.recordCompletedExport({
      menu: 'evaluations',
      format: 'xlsx',
      filters: { partner_id: partnerId, partner_name: partnerName },
      rowCount: detail.evalHist?.length || 0,
    });
  };

  const handleExportPdf = () => {
    if (!detail) return;

    if (typeof onExportPdf === 'function') {
      onExportPdf(detail);
      return;
    }

    exportPartnerEvaluationPdf(detail, generatedAt, generatedDate);
    PartnerExportService.recordCompletedExport({
      menu: 'evaluations',
      format: 'pdf',
      filters: { partner_id: partnerId, partner_name: partnerName },
      rowCount: detail.evalHist?.length || 0,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '8px',
          overflow: 'hidden',
          bgcolor: '#f6f9fc',
          boxShadow: '0 24px 64px rgba(15,23,42,.18)',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#d5ad3f',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 3,
          py: 2,
          color: '#ffffff',
          bgcolor: '#173d61',
          borderBottom: '1px solid rgba(255,255,255,0.18)',
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(255,255,255,0.16)',
                color: '#ffffff',
              }}
            >
              <ArticleIcon fontSize="small" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#ffffff' }}>
              Fiche partenaire
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.78)' }}>
            Générée le {generatedAt}
          </Typography>
        </Box>
        <Tooltip title="Fermer la fiche">
          <IconButton
            aria-label="Fermer la fiche partenaire"
            onClick={onClose}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.28)',
              borderRadius: '8px',
              color: '#ffffff',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.20)' },
              '&:focus-visible': { outline: '2px solid #d5ad3f' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 2, md: 3 }, borderColor: '#cbd8e6', bgcolor: '#f6f9fc' }}>
        {isLoading ? (
          <Stack spacing={1.5} sx={{ alignItems: 'center', py: 4 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Chargement des évaluations...
            </Typography>
          </Stack>
        ) : loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : !detail ? (
          <Alert severity="info">Aucune donnee disponible pour ce partenaire.</Alert>
        ) : (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={heroPaperSx}>
              <Grid container spacing={2.5} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="h4"
                        sx={{ color: '#10263d', fontSize: { xs: 23, sm: 30 }, fontWeight: 800, lineHeight: 1.15 }}
                      >
                        {detail.name}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 1, rowGap: 0.75 }}>
                        <Chip
                          label={formatPartnerMatricule(detail.pid)}
                          size="small"
                          sx={{ ...mutedChipSx, fontFamily: "'DM Mono', monospace" }}
                          variant="outlined"
                        />
                      </Stack>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack
                    direction={{ xs: 'row', md: 'column' }}
                    spacing={1}
                    sx={{ alignItems: { xs: 'center'}, flexWrap: 'wrap', rowGap: 1 }}
                  >
                    <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                      <Typography variant="caption" sx={metaLabelSx}>Situation actuelle</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={normalizeExportSituation(detail.situation) || ''}
                          size="small"
                          sx={{ fontWeight: 700, ...getSituationChipSx(detail.situation) }}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                      <Typography variant="caption" sx={metaLabelSx}>Base source</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={formatPartnerSource(detail.sourceDb)}
                          size="small"
                          sx={{ ...mutedChipSx, color: '#173d61' }}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>

            </Paper>

            <Paper variant="outlined" sx={{ ...cardPaperSx, height: 'auto' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, color: '#173d61' }}>
                <BusinessIcon />
                <Typography sx={{ fontWeight: 800, fontSize: 16 }}>Informations du partenaire</Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}><PartnerInfo label="Statut juridique" value={detail.statutJuridique} /></Grid>
                <Grid item xs={6} sm={4}><PartnerInfo label="NIF" value={detail.nif} /></Grid>
                <Grid item xs={6} sm={4}><PartnerInfo label="STAT" value={detail.stat} /></Grid>
                <Grid item xs={12} sm={4}><PartnerInfo label="Contact" value={detail.contact} /></Grid>
                <Grid item xs={12} sm={4}><PartnerInfo label="Téléphone" value={detail.tel} /></Grid>
                <Grid item xs={12} sm={4}><PartnerInfo label="Email" value={detail.email} /></Grid>
                <Grid item xs={12}><PartnerInfo label="Direction(s)" value={detail.dirs.join(' / ')} /></Grid>
                <Grid item xs={12} sm={6}><PartnerInfo label="Financement(s)" value={detail.financements.join(' / ')} /></Grid>
                <Grid item xs={12} sm={6}><PartnerInfo label="Composante(s)" value={detail.composantes.join(' / ')} /></Grid>
              </Grid>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Paper variant="outlined" sx={cardPaperSx}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box sx={metricIconSx}><AssessmentOutlinedIcon fontSize="small" /></Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={metaLabelSx}>Dernière évaluation</Typography>
                      <Box sx={{ mt: 0.75 }}>
                        <Chip
                          label={detail.evalResult || ''}
                          size="small"
                          sx={{ fontWeight: 700, ...getEvaluationMeta(detail.evalResult).sx }}
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: '#52677c', mt: 0.75 }}>{formatDate(detail.evalDate)}</Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper variant="outlined" sx={cardPaperSx}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box sx={metricIconSx}><BusinessCenterOutlinedIcon fontSize="small" /></Box>
                    <Box>
                      <Typography variant="caption" sx={metaLabelSx}>Marchés</Typography>
                      <Typography variant="h5" sx={{ color: '#10263d', fontWeight: 800, mt: 0.4 }}>{detail.nbMarches}</Typography>
                      <Typography variant="body2" sx={{ color: '#52677c' }}>
                        {detail.marchesEnCours} en cours · {detail.marchesResilies} résilié(s)
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Paper variant="outlined" sx={cardPaperSx}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box sx={metricIconSx}><GroupsOutlinedIcon fontSize="small" /></Box>
                    <Box>
                      <Typography variant="caption" sx={metaLabelSx}>Personnel</Typography>
                      <Typography variant="h5" sx={{ color: '#10263d', fontWeight: 800, mt: 0.4 }}>{detail.personnel.length}</Typography>
                      <Typography variant="body2" sx={{ color: '#52677c' }}>personne(s) enregistrée(s)</Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ ...cardPaperSx, borderColor: '#cbd8e6', height: 'auto' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={metricIconSx}><AssessmentOutlinedIcon fontSize="small" /></Box>
                  <Typography variant="subtitle2" sx={{ ...sectionTitleSx, mb: 0 }}>Historique des évaluations</Typography>
                </Stack>
                <Chip label={`${detail.evalHist.length} entrée(s)`} size="small" sx={mutedChipSx} variant="outlined" />
              </Stack>
              {detail.evalHist.length ? (
                <Stack spacing={0}>
                  {detail.evalHist.map((history, index) => (
                    <Box key={`${history.date || 'no-date'}-${history.result}-${index}`} sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ position: 'relative', width: 18, flexShrink: 0 }}>
                        <Box
                          sx={{
                            bgcolor: index === 0 ? '#d5ad3f' : '#9fb1c3',
                            border: '3px solid #ffffff',
                            borderRadius: '50%',
                            boxShadow: '0 0 0 1px #cbd8e6',
                            height: 12,
                            left: 3,
                            position: 'absolute',
                            top: 17,
                            width: 12,
                            zIndex: 1,
                          }}
                        />
                        {/* {index < detail.evalHist.length - 1 ? (
                          <Box sx={{ bgcolor: '#d5e0eb', bottom: 0, left: 8.5, position: 'absolute', top: 29, width: 1 }} />
                        ) : null} */}
                      </Box>
                      <Paper
                        variant="outlined"
                        sx={{
                          bgcolor: index === 0 ? '#fffcf3' : '#ffffff',
                          borderColor: index === 0 ? '#d5ad3f' : '#d5e0eb',
                          borderRadius: '8px',
                          flex: 1,
                          mb: index < detail.evalHist.length - 1 ? 1.25 : 0,
                          p: 1.25,
                        }}
                      >
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                          <Typography variant="body2" sx={{ color: '#173d61', fontWeight: 700, minWidth: 92 }}>
                            {formatDate(history.date)}
                          </Typography>
                          <Chip
                            label={history.result}
                            size="small"
                            sx={{ fontWeight: 700, ...getEvaluationMeta(history.result).sx }}
                            variant="outlined"
                          />
                          <Typography variant="caption" sx={{ color: '#52677c', flex: 1 }}>
                            Marché : {history.marche || ''}
                          </Typography>
                          {index === 0 ? <Chip label="Dernière" size="small" sx={{ ...mutedChipSx, borderColor: '#d5ad3f' }} variant="outlined" /> : null}
                        </Stack>
                      </Paper>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ bgcolor: '#f8fafc', border: '1px dashed #cbd8e6', borderRadius: '8px', py: 3, textAlign: 'center' }}>
                  <AssessmentOutlinedIcon sx={{ color: '#9fb1c3', mb: 0.75 }} />
                  <Typography variant="body2" sx={{ color: '#52677c' }}>Aucune évaluation enregistrée.</Typography>
                </Box>
              )}
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                ...cardPaperSx,
                borderColor: '#e2e8f0',
                background: '#ffffff',
              }}
            >
              <Typography variant="subtitle2" sx={sectionTitleSx}>
                Contrats / Marchés ({detail.marches.length})
              </Typography>
              <TableContainer sx={{ border: '1px solid #d5e0eb', borderRadius: '8px' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>N° Marche</TableCell>
                      <TableCell sx={tableHeadCellSx}>Composante</TableCell>
                      <TableCell sx={tableHeadCellSx}>Financement</TableCell>
                      <TableCell sx={tableHeadCellSx}>Direction</TableCell>
                      <TableCell sx={tableHeadCellSx} align="right">Nb menages</TableCell>
                      <TableCell sx={tableHeadCellSx}>Debut</TableCell>
                      <TableCell sx={tableHeadCellSx}>Fin</TableCell>
                      <TableCell sx={tableHeadCellSx}>Situation</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.marches.map((market) => (
                      <TableRow key={market.num} hover>
                        <TableCell sx={{ fontFamily: "'DM Mono', monospace", color: '#111827', fontWeight: 700 }}>
                          {market.num}
                        </TableCell>
                        <TableCell>{market.composante || '—'}</TableCell>
                        <TableCell>{market.financement || '—'}</TableCell>
                        <TableCell>{market.direction || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: "'DM Mono', monospace" }}>
                          {formatCount(market.beneficiaires)}
                        </TableCell>
                        <TableCell>{formatDate(market.dateDebut)}</TableCell>
                        <TableCell>{formatDate(market.dateFin)}</TableCell>
                        <TableCell>
                          <Chip
                            label={normalizeExportSituation(getSituationValue(new Set([market.situation])))}
                            size="small"
                            variant="outlined"
                            sx={getSituationChipSx(getSituationValue(new Set([market.situation])))}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 700 }}>
                        TOTAL BENEFICIAIRES
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: "'DM Mono', monospace", fontWeight: 700 }}>
                        {formatCount(detail.totalBeneficiaires)}
                      </TableCell>
                      <TableCell colSpan={3} />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {detail.personnel.length ? (
              <Paper
                variant="outlined"
                sx={{
                  ...cardPaperSx,
                  borderColor: '#e2e8f0',
                  background: '#ffffff',
                }}
              >
                <Typography variant="subtitle2" sx={sectionTitleSx}>
                  Personnel ({detail.personnel.length})
                </Typography>
                <Grid container spacing={1.25} sx={{ mb: 1.5 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Filtrer par nom"
                      onChange={(event) => setPersonnelNameFilter(event.target.value)}
                      size="small"
                      value={personnelNameFilter}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#ffffff',
                          borderRadius: '6px',
                          fontSize: 12,
                          minHeight: 40,
                        },
                        '& .MuiInputLabel-root': { fontSize: 12 },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Filtrer par poste"
                      onChange={(event) => setPersonnelPositionFilter(event.target.value)}
                      size="small"
                      value={personnelPositionFilter}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#ffffff',
                          borderRadius: '6px',
                          fontSize: 12,
                          minHeight: 40,
                        },
                        '& .MuiInputLabel-root': { fontSize: 12 },
                      }}
                    />
                  </Grid>
                </Grid>
                <Typography variant="caption" sx={{ color: '#52677c', display: 'block', mb: 1 }}>
                  {filteredPersonnel.length} personnel(s) trouvé(s)
                </Typography>
                {filteredPersonnel.length ? (
                <Grid container spacing={1}>
                  {paginatedPersonnel.map((person) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={person.nom}>
                      <Paper variant="outlined" sx={miniPaperSx}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#111827' }}>
                          {person.nom}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.35,
                            color: '#475569',
                            fontFamily: "'DM Mono', monospace",
                            fontWeight: 700,
                          }}
                        >
                          {formatPersonnelMatricule(person)}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary">
                            {person.fonction || '—'}
                          </Typography>
                          {/* <Chip
                            label={normalizeExportSituation(getSituationValue(new Set([person.situation])))}
                            size="small"
                            variant="outlined"
                            sx={getSituationChipSx(getSituationValue(new Set([person.situation])))}
                          /> */}
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                ) : (
                  <Alert severity="info">Aucun personnel ne correspond aux filtres.</Alert>
                )}
                {personnelPageCount > 1 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                      count={personnelPageCount}
                      onChange={(_, page) => setPersonnelPage(page)}
                      page={personnelPage}
                      shape="rounded"
                      size="small"
                      sx={{
                        '& .MuiPaginationItem-root.Mui-selected': {
                          bgcolor: '#173d61',
                          color: '#ffffff',
                        },
                        '& .MuiPaginationItem-root:focus-visible': {
                          outline: '2px solid #d5ad3f',
                        },
                      }}
                    />
                  </Box>
                ) : null}
              </Paper>
            ) : null}

            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              {formatPartnerSource(detail.sourceDb)} · Fiche generee le {generatedAt}
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          bgcolor: '#ffffff',
          borderTop: '1px solid #cbd8e6',
          bottom: 0,
          display: 'flex',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: 1,
          justifyContent: 'space-between',
          px: { xs: 2, md: 3 },
          py: 1.5,
          position: 'sticky',
          zIndex: 2,
        }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          variant="outlined"
          startIcon={<CloseIcon />}
          sx={{
            borderColor: '#9fb1c3',
            borderRadius: '6px',
            color: '#173d61',
            textTransform: 'none',
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          Fermer
        </Button>
        <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Button
            onClick={handleExportExcel}
            variant="outlined"
            disabled={!detail}
            startIcon={<TableChartIcon />}
            sx={{
              bgcolor: '#f0fdf4',
              borderColor: '#86efac',
              borderRadius: '6px',
              color: '#047857',
              textTransform: 'none',
              flex: { xs: 1, sm: 'initial' },
              '&:hover': { bgcolor: '#dcfce7', borderColor: '#34d399' },
            }}
          >
            Excel
          </Button>
          <Button
            onClick={handleExportPdf}
            variant="contained"
            disabled={!detail}
            startIcon={<PictureAsPdfIcon />}
            endIcon={<FileDownloadIcon />}
            sx={{
              bgcolor: '#173d61',
              borderRadius: '6px',
              boxShadow: 'none',
              textTransform: 'none',
              flex: { xs: 1, sm: 'initial' },
              '&:hover': { bgcolor: '#102f4c', boxShadow: 'none' },
            }}
          >
            PDF
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default PartnerEvalution;
